// src/components/invoices/InvoiceLayout.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';

// Fonction utilitaire pour résoudre l'URL complète du logo
const getLogoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const InvoiceLayout = ({ facture, client, cabinetInfo }) => {
  const { tenantType } = useAuth();
  const isMedical = tenantType === 'cabinet_medical';
  const isGlobal = facture.isGlobal === true;
  if (!facture) return null;

  const calculateTotals = () => {
    if (!facture.items || facture.items.length === 0) {
      return { subtotal: 0, totalTVA: 0, timbre: 0, total: 0 };
    }

    const subtotal =
      facture.totalHT ||
      facture.items.reduce((sum, item) => sum + (item.totalHT || 0), 0);
    const totalTVA =
      facture.totalTVA ||
      facture.items.reduce((sum, item) => sum + (item.totalTVA || 0), 0);
    const timbre = facture.timbreFiscal || 0;
    const total =
      facture.total ||
      facture.totalTTC ||
      subtotal + totalTVA + timbre;

    return {
      subtotal: subtotal.toFixed(3),
      totalTVA: totalTVA.toFixed(3),
      timbre: timbre.toFixed(3),
      total: total.toFixed(3),
    };
  };

  const totals = calculateTotals();

  const formatDate = (dateString) => {
    if (!dateString) return '--/--/----';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const cabinet = cabinetInfo || {
    name: 'CABINET',
    address: '123 Rue des Avocats',
    city: '1000 Tunis',
    phone: '+216 71 123 456',
    email: 'contact@cabinet.tn',
    taxId: 'MF12345678',
    rib: 'TN59 1234 5678 9012 3456 7890',
    bank: 'Banque de Tunisie',
    paymentTerms: 'Paiement à 30 jours',
    logo: null,
  };

  const logoSrc = getLogoUrl(cabinet.logo);
  console.log('CABINET LOGO:', cabinet.logo, 'LOGO SRC:', logoSrc);

  return (
    <div
      className="invoice-layout"
      style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontSize: '12px',
        padding: '20px',
        maxWidth: '794px',
        margin: '0 auto',
        backgroundColor: 'white',
      }}
    >
      {/* En-tête avec Logo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px',
          borderBottom: '2px solid #333',
          paddingBottom: '20px',
        }}
      >
        <div>
          {logoSrc && (
            <div style={{ marginBottom: '15px' }}>
              <img 
                src={logoSrc} 
                alt="Logo Cabinet" 
                style={{ 
                  maxHeight: '80px', 
                  maxWidth: '200px', 
                  objectFit: 'contain' 
                }}
                crossOrigin="anonymous" 
              />
            </div>
          )}

          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1e40af',
              marginBottom: '4px',
              marginTop: '0'
            }}
          >
            {isGlobal ? "NOTE D'HONORAIRE GLOBALE" : (isMedical ? "NOTE D'HONORAIRE" : 'FACTURE')}
          </h1>
          <div
            style={{
              backgroundColor: '#e0f2fe',
              color: '#1e40af',
              padding: '4px 12px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            <strong>N° {facture.number || '--'}</strong>
          </div>
        </div>

        <div style={{ textAlign: 'right', marginTop: logoSrc ? '10px' : '0' }}>
          <div
            style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '4px',
            }}
          >
            {cabinet.name}
          </div>
          <div style={{ color: '#4b5563', fontSize: '12px' }}>
            <p style={{ margin: '2px 0' }}>{cabinet.address}</p>
            <p style={{ margin: '2px 0' }}>{cabinet.city}</p>
            <p style={{ margin: '2px 0' }}>Tél: {cabinet.phone}</p>
            <p style={{ margin: '2px 0' }}>Email: {cabinet.email}</p>
            {cabinet.taxId && (
              <p style={{ margin: '2px 0' }}>
                Matricule Fiscal: {cabinet.taxId}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Infos client + détails facture */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '8px',
          }}
        >
          <h3
            style={{
              fontWeight: 'bold',
              color: '#374151',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          >
            {isGlobal ? 'RÉFÉRENCE' : 'CLIENT'}
          </h3>
          <div style={{ fontSize: '12px', color: '#4b5563' }}>
            <p style={{ fontWeight: '600', margin: '2px 0' }}>
              {client?.companyName || (isGlobal ? 'Note globale - Patients listés ci-dessous' : 'Client non spécifié')}
            </p>
            {client?.contactName && (
              <p style={{ margin: '2px 0' }}>Contact: {client.contactName}</p>
            )}
            {client?.address && (
              <p style={{ margin: '2px 0' }}>{client.address}</p>
            )}
            {client?.taxId && (
              <p style={{ margin: '2px 0' }}>Matricule Fiscal: {client.taxId}</p>
            )}
            {client?.email && (
              <p style={{ margin: '2px 0' }}>{client.email}</p>
            )}
            {client?.phone && (
              <p style={{ margin: '2px 0' }}>{client.phone}</p>
            )}
          </div>
        </div>

        <div
          style={{
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '8px',
          }}
        >
            <h3
              style={{
                fontWeight: 'bold',
                color: '#374151',
                marginBottom: '8px',
                fontSize: '14px',
              }}
            >
              DÉTAILS {isGlobal ? "NOTE D'HONORAIRE GLOBALE" : (isMedical ? "NOTE D'HONORAIRE" : 'FACTURE')}
            </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              fontSize: '12px',
              color: '#4b5563',
            }}
          >
            <div>
              <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0' }}>
                Date facturation
              </p>
              <p style={{ fontWeight: '600', margin: '2px 0' }}>
                {formatDate(facture.date)}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0' }}>
                Date échéance
              </p>
              <p style={{ fontWeight: '600', margin: '2px 0' }}>
                {formatDate(facture.dueDate)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des articles */}
      <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              {isGlobal && (
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                  Patient
                </th>
              )}
              <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                Description
              </th>
              <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                Qté
              </th>
              <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                Prix HT
              </th>
              <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                TVA %
              </th>
              <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                Total HT
              </th>
              <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                Total TTC
              </th>
            </tr>
          </thead>
          <tbody>
            {facture.items && facture.items.length > 0 ? (
              facture.items.map((item, index) => (
                <tr key={index} style={{ border: '1px solid #ddd' }}>
                  {isGlobal && (
                    <td style={{ padding: '12px', border: '1px solid #ddd', fontWeight: '600' }}>
                      {item.patientName || '--'}
                    </td>
                  )}
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                    {item.description || '--'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>
                    {item.quantity || item.qty || 1}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>
                    {(item.priceHT || item.price || 0).toFixed(2)} D
                  </td>
                  
                  {/* --- CORRECTION ICI : Lecture directe de tvaRate (Backend) ou tax (Formulaire) --- */}
                  <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>
                    {(() => {
                        const val = item.tvaRate !== undefined 
                          ? item.tvaRate 
                          : (item.tax !== undefined ? item.tax : item.tva);
                        
                        const numVal = parseFloat(val) || 0;

                        if (numVal > 0 && numVal <= 1) {
                          return `${Math.round(numVal * 100)}%`;
                        }
                        return `${Math.round(numVal)}%`;
                    })()}
                  </td>
                  {/* --------------------------------------------------------------------------------- */}

                  <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: '600' }}>
                    {(item.totalHT || 0).toFixed(2)} D
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', color: '#1e40af' }}>
                    {(item.totalTTC || 0).toFixed(2)} D
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isGlobal ? 7 : 6} style={{ padding: '16px', textAlign: 'center', color: '#6b7280', border: '1px solid #ddd' }}>
                  Aucun article dans cette facture
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totaux */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
        <div style={{ width: '33%', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#4b5563' }}>Sous-total HT</span>
            <span style={{ fontWeight: '600' }}>{totals.subtotal} D</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#4b5563' }}>TVA</span>
            <span style={{ fontWeight: '600' }}>{totals.totalTVA} D</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#4b5563' }}>Timbre fiscal</span>
            <span style={{ fontWeight: '600' }}>{totals.timbre} D</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid #333', fontWeight: 'bold', color: '#1e40af', fontSize: '14px' }}>
            <span>Total TTC</span>
            <span>{totals.total} D</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {facture.notes && (
        <div style={{ marginBottom: '16px', fontSize: '12px' }}>
          <h4 style={{ fontWeight: 'bold', color: '#374151', marginBottom: '4px' }}>Notes</h4>
          <div style={{ backgroundColor: '#fef9c3', padding: '12px', borderRadius: '4px', border: '1px solid #fde047' }}>
            {facture.notes}
          </div>
        </div>
      )}

      {/* Conditions de paiement */}
      <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', fontSize: '12px', marginBottom: '24px' }}>
        <h4 style={{ fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>Conditions de paiement</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', color: '#4b5563' }}>
          <div>
            <p><strong>Mode de paiement:</strong> Virement bancaire</p>
            {cabinet.rib && <p><strong>RIB:</strong> {cabinet.rib}</p>}
            {cabinet.bank && <p><strong>Banque:</strong> {cabinet.bank}</p>}
          </div>
          <div>
            <p><strong>Délai de paiement:</strong> {cabinet.paymentTerms || '30 jours à compter de la date de facturation'}</p>
            <p><strong>Escompte:</strong> Aucun</p>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '24px', borderTop: '1px solid #ccc', fontSize: '12px' }}>
        <div style={{ textAlign: 'center', width: '45%' }}>
          <div style={{ fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>Le client</div>
          <div style={{ height: '60px', borderTop: '1px solid #000', marginTop: '8px', paddingTop: '4px', color: '#6b7280' }}>
            Signature et cachet
          </div>
        </div>
        <div style={{ textAlign: 'center', width: '45%' }}>
          <div style={{ fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>Le cabinet</div>
          <div style={{ height: '60px', borderTop: '1px solid #000', marginTop: '8px', paddingTop: '4px', color: '#6b7280' }}>
            Signature et cachet
          </div>
        </div>
      </div>

      {/* Pied de page */}
      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #ccc', textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>
        <p>Cette {isGlobal ? "note d'honoraire globale" : (isMedical ? "note d'honoraire" : 'facture')} est électronique et ne nécessite pas de signature manuscrite</p>
        <p>Merci de votre confiance !</p>
      </div>
    </div>
  );
};

InvoiceLayout.propTypes = {
  facture: PropTypes.object.isRequired,
  client: PropTypes.object,
  cabinetInfo: PropTypes.object,
};

InvoiceLayout.defaultProps = {
  client: null,
  cabinetInfo: null,
};

export default InvoiceLayout;